import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ArrowLeft, Settings, Smartphone, Bell, Share2 } from 'lucide-react';
import NotificationSettings from './NotificationSettings';
import PWAStatus from './PWAStatus';
import ShareButton from './ShareButton';
import { usePWA } from '../hooks/usePWA';

const PWASettings = () => {
  const navigate = useNavigate();
  const { isInstalled, isInstallable, installApp } = usePWA();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(236,72,153,0.1),transparent_70%)]"></div>
      
      <div className="relative z-10 min-h-screen p-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8 pt-8">
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <ArrowLeft className="mr-2" size={16} />
              Back
            </Button>
            
            <div className="text-center">
              <h1 className="text-2xl font-bold text-white flex items-center">
                <Settings className="mr-2 text-rose-400" size={24} />
                App Settings
              </h1>
              <p className="text-gray-300 text-sm mt-1">Manage your PWA experience</p>
            </div>
            
            <div className="w-20"></div> {/* Spacer for centering */}
          </div>

          <div className="space-y-6">
            {/* Installation Status */}
            <Card className="bg-white/5 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Smartphone className="mr-2 text-rose-400" size={20} />
                  App Installation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isInstalled ? (
                  <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <div>
                      <p className="text-green-400 font-medium">App Installed ✓</p>
                      <p className="text-gray-300 text-sm">You're using the installed version</p>
                    </div>
                    <div className="text-green-400 text-2xl">📱</div>
                  </div>
                ) : isInstallable ? (
                  <div className="flex items-center justify-between p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <div>
                      <p className="text-blue-400 font-medium">Ready to Install</p>
                      <p className="text-gray-300 text-sm">Get the full app experience</p>
                    </div>
                    <Button
                      onClick={installApp}
                      className="bg-rose-500 hover:bg-rose-600 text-white"
                    >
                      Install App
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-4 bg-gray-500/10 border border-gray-500/20 rounded-lg">
                    <div>
                      <p className="text-gray-400 font-medium">Browser Version</p>
                      <p className="text-gray-300 text-sm">Using web version</p>
                    </div>
                    <div className="text-gray-400 text-2xl">🌐</div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notification Settings */}
            <Card className="bg-white/5 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Bell className="mr-2 text-rose-400" size={20} />
                  Notifications
                </CardTitle>
              </CardHeader>
              <CardContent>
                <NotificationSettings />
              </CardContent>
            </Card>

            {/* PWA Status */}
            <Card className="bg-white/5 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Settings className="mr-2 text-rose-400" size={20} />
                  App Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PWAStatus />
              </CardContent>
            </Card>

            {/* Share Settings */}
            <Card className="bg-white/5 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Share2 className="mr-2 text-rose-400" size={20} />
                  Share App
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Share Shot Clock Dating</p>
                    <p className="text-gray-300 text-sm">Invite friends to join the experience</p>
                  </div>
                  <ShareButton 
                    title="Check out Shot Clock Dating!"
                    text="AI-powered speed dating with video calls and smart matching. Join me on Shot Clock Dating!"
                    className="bg-rose-500 hover:bg-rose-600 text-white"
                  />
                </div>
              </CardContent>
            </Card>

            {/* App Info */}
            <Card className="bg-white/5 backdrop-blur-md border-white/10">
              <CardContent className="pt-6">
                <div className="text-center space-y-2">
                  <div className="flex justify-center">
                    <img 
                      src="/icon-96x96.png" 
                      alt="Shot Clock Dating" 
                      className="w-16 h-16 rounded-xl"
                    />
                  </div>
                  <h3 className="text-white font-semibold">Shot Clock Dating</h3>
                  <p className="text-gray-300 text-sm">Progressive Web App v1.1.0</p>
                  <p className="text-gray-400 text-xs">AI-powered speed dating platform</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWASettings;